import { useEffect, useState } from 'react'
import { Box, Chip, Collapse, LinearProgress, Paper, Stack, Typography } from '@mui/material'
import { FunctionsHttpError } from '@supabase/supabase-js'
import { supabase } from '../lib/supabase'
import { useFiltros } from '../state/FiltrosContext'
import { BotaoExpandir } from './BotaoExpandir'

type Achado = { tipo: 'resumo' | 'destaque' | 'alerta' | 'oportunidade'; texto: string }
type Leitura = {
  achados: Achado[]
  texto: string
  acoes: string[]
  fonte: 'motor' | 'ia'
  modelo: string | null
  iaDisponivel: boolean
  motivoSemIa?: string
}

type Props = {
  painel: 'trafego_pago' | 'organico' | 'redes_sociais'
  /** Recortes próprios do painel, além de período e marca. */
  filtros?: { plataforma?: string; campanhaId?: string; rede?: string }
}

const ROTULO: Record<Achado['tipo'], { texto: string; cor: 'default' | 'warning' | 'success' | 'info' }> = {
  alerta: { texto: 'Alerta', cor: 'warning' },
  destaque: { texto: 'Destaque', cor: 'success' },
  oportunidade: { texto: 'Oportunidade', cor: 'info' },
  resumo: { texto: 'Resumo', cor: 'default' },
}
const ORDEM: Achado['tipo'][] = ['alerta', 'oportunidade', 'destaque', 'resumo']

async function pedir(corpo: Record<string, unknown>): Promise<Leitura> {
  const { data, error } = await supabase.functions.invoke('leitura-ia', { body: corpo })
  if (error) {
    const detalhe = error instanceof FunctionsHttpError ? await error.context.json().catch(() => null) : null
    throw new Error(detalhe?.erro ?? error.message)
  }
  return data as Leitura
}

/**
 * Leitura do painel: primeiro a do motor (na hora, só com os números do painel); se houver
 * IA configurada (Configurações → IA), a versão escrita por ela substitui quando chega — já conferida no
 * servidor, número por número. Os achados de onde a leitura saiu ficam a um clique.
 */
export function LeituraIA({ painel, filtros }: Props) {
  const { intervalo, marcaId } = useFiltros()
  const [leitura, setLeitura] = useState<Leitura | null>(null)
  const [escrevendo, setEscrevendo] = useState(false)
  const [erro, setErro] = useState<string | null>(null)
  const [verAchados, setVerAchados] = useState(false)
  const { plataforma, campanhaId, rede } = filtros ?? {}

  useEffect(() => {
    let cancelado = false
    const corpo = {
      painel,
      inicio: intervalo.inicio,
      fim: intervalo.fim,
      marca_id: marcaId || null,
      plataforma: plataforma || null,
      campanha_id: campanhaId || null,
      rede: rede || null,
    }
    setErro(null)
    setEscrevendo(false)
    ;(async () => {
      try {
        const motor = await pedir({ ...corpo, ia: false })
        if (cancelado) return
        setLeitura(motor)
        if (!motor.iaDisponivel) return
        setEscrevendo(true)
        const escrita = await pedir({ ...corpo, ia: true })
        if (!cancelado) setLeitura(escrita)
      } catch (e) {
        if (!cancelado) setErro((e as Error).message)
      } finally {
        if (!cancelado) setEscrevendo(false)
      }
    })()
    return () => {
      cancelado = true
    }
  }, [painel, intervalo, marcaId, plataforma, campanhaId, rede])

  if (erro) {
    return (
      <Typography variant="caption" color="text.secondary">
        Leitura indisponível: {erro}
      </Typography>
    )
  }
  if (!leitura) return <LinearProgress sx={{ borderRadius: 1 }} />

  const achados = [...leitura.achados].sort((a, b) => ORDEM.indexOf(a.tipo) - ORDEM.indexOf(b.tipo))
  const ia = leitura.fonte === 'ia'

  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Stack direction="row" alignItems="center" spacing={1} useFlexGap flexWrap="wrap" sx={{ mb: 1 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 700, fontFamily: '"Outfit", sans-serif', mr: 'auto' }}>
          Leitura
        </Typography>
        {escrevendo ? (
          <Chip size="small" label="A IA está escrevendo…" variant="outlined" />
        ) : (
          <Chip
            size="small"
            variant="outlined"
            color={ia ? 'primary' : 'default'}
            label={ia ? 'Escrita pela IA · números conferidos' : 'Leitura automática'}
          />
        )}
      </Stack>

      <Typography variant="body1" sx={{ lineHeight: 1.6, opacity: escrevendo ? 0.7 : 1, transition: 'opacity 160ms ease' }}>
        {leitura.texto}
      </Typography>

      {leitura.acoes.length ? (
        <Box component="ul" sx={{ mt: 1.5, mb: 0, pl: 2.5 }}>
          {leitura.acoes.map((a) => (
            <Typography component="li" variant="body2" key={a} sx={{ mb: 0.5 }}>
              {a}
            </Typography>
          ))}
        </Box>
      ) : null}

      <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 1.5 }} useFlexGap flexWrap="wrap">
        <BotaoExpandir aberto={verAchados} onClick={() => setVerAchados((v) => !v)} icone="document">
          {verAchados ? 'Esconder os achados' : `Ver os ${achados.length} achados por trás da leitura`}
        </BotaoExpandir>
        {!ia && leitura.motivoSemIa ? (
          <Typography variant="caption" color="text.secondary">
            Sem a versão da IA: {leitura.motivoSemIa}.
          </Typography>
        ) : null}
        {ia ? (
          <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>
            A IA só reescreve os achados; confira antes de agir.
          </Typography>
        ) : null}
      </Stack>

      <Collapse in={verAchados}>
        <Stack spacing={1} sx={{ mt: 1 }}>
          {achados.map((a) => (
            <Stack key={a.texto} direction="row" spacing={1} alignItems="flex-start">
              <Chip size="small" label={ROTULO[a.tipo].texto} color={ROTULO[a.tipo].cor} variant="outlined" sx={{ minWidth: 104 }} />
              <Typography variant="body2" sx={{ pt: 0.25 }}>
                {a.texto}
              </Typography>
            </Stack>
          ))}
        </Stack>
      </Collapse>
    </Paper>
  )
}
